/**
 * Three-dimensional geometry for the mapping pipeline.
 *
 * The rest of `dsp/` reasons about a single number - the delay to a reflector.
 * Everything here exists because a map needs the other two: where the phone
 * was, and which way it was facing, at the instant each chirp went out.
 *
 * World frame is the renderer's frame: +X right, +Y up, +Z toward the viewer,
 * right-handed, origin at the centre of the scan. Device frame is the W3C
 * DeviceOrientation frame: +X out the right edge of the screen, +Y out the
 * top edge, +Z out of the glass toward the user.
 */

export type Vec3 = [number, number, number];
/** Row-major 3x3, so `m[row * 3 + col]`. */
export type Mat3 = Float64Array;

export function vec3(x = 0, y = 0, z = 0): Vec3 {
  return [x, y, z];
}

export function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

export function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function scale(a: Vec3, k: number): Vec3 {
  return [a[0] * k, a[1] * k, a[2] * k];
}

export function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

export function length(a: Vec3): number {
  return Math.hypot(a[0], a[1], a[2]);
}

export function distance(a: Vec3, b: Vec3): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

export function normalize3(a: Vec3): Vec3 {
  const n = length(a);
  return n > 1e-12 ? [a[0] / n, a[1] / n, a[2] / n] : [0, 0, 0];
}

/** Rotate a device-frame vector into the world frame. */
export function applyMat3(m: Mat3, v: Vec3): Vec3 {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ];
}

export function identity3(): Mat3 {
  return Float64Array.from([1, 0, 0, 0, 1, 0, 0, 0, 1]);
}

/** Rotation about the world Y (up) axis, in radians. */
export function rotationY(angle: number): Mat3 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return Float64Array.from([c, 0, s, 0, 1, 0, -s, 0, c]);
}

/**
 * DeviceOrientation angles to a device-to-world rotation.
 *
 * The W3C convention is an intrinsic Z-X'-Y'' Tait-Bryan sequence about
 * alpha, beta, gamma, producing a matrix that maps the device frame into the
 * Earth frame with axes (east, north, up). The renderer wants Y up, so the
 * last step swaps into (east, up, south) - still right-handed, and a rotation
 * of the whole map about the vertical axis is invisible in a room nobody
 * surveyed against true north anyway.
 *
 * All three angles arrive in degrees, which is what the event carries.
 */
export function orientationToMatrix(alphaDeg: number, betaDeg: number, gammaDeg: number): Mat3 {
  const a = (alphaDeg * Math.PI) / 180;
  const b = (betaDeg * Math.PI) / 180;
  const g = (gammaDeg * Math.PI) / 180;
  const cA = Math.cos(a);
  const sA = Math.sin(a);
  const cB = Math.cos(b);
  const sB = Math.sin(b);
  const cG = Math.cos(g);
  const sG = Math.sin(g);

  // Device -> Earth (east, north, up).
  const e = [cA * cG - sA * sB * sG, -cB * sA, cA * sG + cG * sA * sB];
  const n = [cG * sA + cA * sB * sG, cA * cB, sA * sG - cA * cG * sB];
  const u = [-cB * sG, sB, cB * cG];

  // Earth (east, north, up) -> world (x=east, y=up, z=-north).
  return Float64Array.from([e[0], e[1], e[2], u[0], u[1], u[2], -n[0], -n[1], -n[2]]);
}

/**
 * Candidate acoustic boresights, in the device frame.
 *
 * Which way a phone "points" acoustically is not a thing the web platform
 * will tell you, and it is not the same on two handsets: the loudspeaker
 * fires out the bottom edge on most, out the earpiece on some, and the
 * baffling that produces any directivity at all is the body of the phone
 * rather than the driver. So the axis is not assumed - it is one of these
 * six, chosen after the fact by whichever one focuses the map best.
 */
/**
 * Which way the two-way response is taken to point.
 *
 * This used to be searched over six axes and is now a list of one, which is an
 * admission that the search was costing more than it bought. It enters the
 * reconstruction only as a soft weight - how much a look counts towards a
 * surface it is facing away from - and the comparison between candidates is
 * correspondingly shallow, so it was decided by noise. Choosing the bottom
 * edge instead of the back sent the fitted aperture to 10 cm and the room with
 * it: with the phone held out and nodding, an axis pointing down the handset
 * weights the floor above everything vertical, the walls stop paying for the
 * radius that would resolve them, and the search settles on a trajectory that
 * barely moves.
 *
 * The back of the handset is the defensible choice. Whichever edge the driver
 * sits behind, the body of the phone is the baffle, and in the gesture the app
 * asks for that body has the room in front of it and the user behind it. It is
 * also the axis the trajectory is built on, so the beam and the aperture now
 * agree about which way the phone was facing.
 */
export const BORESIGHT_CANDIDATES: { id: string; label: string; axis: Vec3 }[] = [
  { id: 'back', label: 'out of the back (camera side)', axis: [0, 0, -1] },
];

export interface Plane {
  /** Unit normal. */
  normal: Vec3;
  /** Signed offset: a point p is on the plane when dot(normal, p) = offset. */
  offset: number;
}

export function planeDistance(plane: Plane, p: Vec3): number {
  return dot(plane.normal, p) - plane.offset;
}

/** The plane through `point` with the given normal, normal re-normalised. */
export function planeFrom(normal: Vec3, point: Vec3): Plane {
  const n = normalize3(normal);
  return { normal: n, offset: dot(n, point) };
}

/**
 * The mirror plane implied by a source and its image.
 *
 * This is the whole reason echoes carry geometry rather than just distance.
 * A flat surface reflecting a source is exactly equivalent to a second source
 * at the mirror position, so an echo is a direct path from an image source -
 * and the surface is the perpendicular bisector between the two. Recovering
 * one image source therefore recovers a whole wall, not a point on it.
 */
export function mirrorPlane(source: Vec3, image: Vec3): Plane | null {
  const d = sub(image, source);
  const len = length(d);
  if (len < 1e-9) return null;
  const normal = scale(d, 1 / len);
  const midpoint = scale(add(source, image), 0.5);
  return { normal, offset: dot(normal, midpoint) };
}

/** Reflect a point in a plane. */
export function reflectPoint(plane: Plane, p: Vec3): Vec3 {
  const d = planeDistance(plane, p);
  return sub(p, scale(plane.normal, 2 * d));
}

/** Two horizontal unit vectors spanning the plane, for drawing and for boxes. */
export function planeBasis(normal: Vec3): { u: Vec3; v: Vec3 } {
  const n = normalize3(normal);
  const seed: Vec3 = Math.abs(n[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  const u = normalize3(cross(seed, n));
  const v = cross(n, u);
  return { u, v };
}

/**
 * The shortest rotation carrying `from` onto `to`, by Rodrigues.
 *
 * Used to synthesise a device orientation from a direction the phone is meant
 * to be pointing. Roll about the boresight is left unspecified because the
 * beam model is axially symmetric, so it cannot matter.
 */
export function rotationBetween(from: Vec3, to: Vec3): Mat3 {
  const a = normalize3(from);
  const b = normalize3(to);
  const c = dot(a, b);
  if (c > 1 - 1e-9) return identity3();

  let axis = cross(a, b);
  let s = length(axis);
  if (s < 1e-9) {
    // Antiparallel: any perpendicular axis turns one onto the other.
    const seed: Vec3 = Math.abs(a[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
    axis = normalize3(cross(a, seed));
    s = 0;
  } else {
    axis = scale(axis, 1 / s);
  }

  const angle = Math.acos(Math.max(-1, Math.min(1, c)));
  const ct = Math.cos(angle);
  const st = Math.sin(angle);
  const t = 1 - ct;
  const [x, y, z] = axis;
  return Float64Array.from([
    t * x * x + ct, t * x * y - st * z, t * x * z + st * y,
    t * x * y + st * z, t * y * y + ct, t * y * z - st * x,
    t * x * z - st * y, t * y * z + st * x, t * z * z + ct,
  ]);
}
